/* ─────────────────────────────────────────────────────────────────────────
   KIMDYMA · eb-motion.js — reveal in AND out, parallax, counters. v4.0

   Hand-written; it REPLACES the v1.0 controller, which revealed once and then
   stopped watching. The parallax and counter code below is carried over from
   v1.0 unchanged.

   WHAT REVEALS. Nothing has to be tagged by hand. A [data-reveal-group] stages
   its children with --i; a section's head and the direct children of its
   inner get `rise` by default. A section built from system containers is
   staged without knowing this file exists, and a per-element list is a list
   that goes stale the first time a section is edited.

   IN AND OUT. An element entering the viewport gets .is-revealed. One that
   has fully LEFT gets .is-leaving — an opacity-only fade, per Rule 1 — and
   once the fade completes the class is dropped and the element returns to its
   entrance pose out of sight, ready to rise again. Scrolling back up finds the
   page arriving, not already there.

   ABOVE THE FOLD IS NOT OBSERVED. An observer fires for elements on screen
   at load, but only after the first frame — a visible blink on the hero.
   Anything in the first viewport is revealed on the next frame instead.

   Everything here is additive: remove the file and every element is simply
   visible.
   ───────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';
  if (window.ebMotionLoaded) return;
  window.ebMotionLoaded = true;

  var root = document.documentElement;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var slice = function (l) { return Array.prototype.slice.call(l); };
  function motionOff() { return reduce.matches || root.getAttribute('data-motion') === 'off'; }

  /* ── what reveals ────────────────────────────────────────────────────── */
  var GROUPS = ['.eb-grid--cards', '.eb-rail', '.eb-panels', '.eb-creators',
                '.eb-footer__nav', '.eb-quotes__stage'];

  function stage(group, kind) {
    slice(group.children).forEach(function (el, i) {
      el.style.setProperty('--i', i);
      if (!el.hasAttribute('data-reveal')) el.setAttribute('data-reveal', kind || 'rise');
    });
  }

  function tag(scope) {
    scope = scope || document;
    slice(scope.querySelectorAll('[data-reveal-group]')).forEach(function (g) {
      if (g.dataset.revealTagged) return;
      g.dataset.revealTagged = '1';
      stage(g, g.getAttribute('data-reveal-group'));
    });
    slice(scope.querySelectorAll('.eb-section')).forEach(function (section) {
      var inner = section.querySelector('.eb-section__inner');
      if (!inner || inner.dataset.revealTagged) return;
      inner.dataset.revealTagged = '1';
      var head = section.querySelector('.eb-section__head');
      if (head && !head.hasAttribute('data-reveal')) head.setAttribute('data-reveal', 'rise');
      slice(inner.children).forEach(function (el, i) {
        if (el === head || el.hasAttribute('data-reveal')) return;
        el.setAttribute('data-reveal', 'rise');
        el.style.setProperty('--i', i);
      });
      GROUPS.forEach(function (sel) {
        slice(section.querySelectorAll(sel)).forEach(function (g) {
          if (g.dataset.revealTagged) return;
          g.dataset.revealTagged = '1';
          g.setAttribute('data-reveal-group', 'rise');
          stage(g, 'rise');
        });
      });
    });
  }

  /* ── in and out ──────────────────────────────────────────────────────── */
  var io = null;
  function leaveDone(el) {
    el.classList.remove('is-leaving');
    el.removeEventListener('transitionend', el._ebLeave);
    el._ebLeave = null;
  }
  function reveal(el) {
    if (el._ebLeave) leaveDone(el);
    el.classList.add('is-revealed');
  }
  function leave(el) {
    if (!el.classList.contains('is-revealed')) return;
    el.classList.remove('is-revealed');
    el.classList.add('is-leaving');
    var done = function (e) { if (!e || e.target === el) leaveDone(el); };
    el._ebLeave = done;
    el.addEventListener('transitionend', done);
    // A transition that never fires (display:none mid-fade) must not leave
    // the element stuck; the fallback is the exit duration plus a frame.
    window.setTimeout(function () { if (el._ebLeave === done) leaveDone(el); }, 520);
  }

  function observer() {
    if (io || !('IntersectionObserver' in window)) return io;
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.intersectionRatio >= 0.12 || (entry.isIntersecting && entry.intersectionRatio > 0
            && entry.boundingClientRect.height > window.innerHeight * .8)) {
          reveal(entry.target);
        } else if (!entry.isIntersecting) {
          leave(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: [0, 0.12] });
    return io;
  }

  function observe(scope) {
    var targets = slice((scope || document).querySelectorAll('[data-reveal]'));
    if (motionOff() || !observer()) {
      targets.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }
    var fold = window.innerHeight;
    targets.forEach(function (el) {
      if (el.dataset.ebObserved) return;
      el.dataset.ebObserved = '1';
      if (el.getBoundingClientRect().top < fold) {
        requestAnimationFrame(function () { reveal(el); });
      }
      io.observe(el);
    });
  }

  /* ── parallax (v1.0, unchanged) ──────────────────────────────────────── */
  function initParallax() {
    if (motionOff()) return;
    var layers = slice(document.querySelectorAll('[data-parallax]'));
    if (!layers.length) return;
    var ticking = false;
    function frame() {
      var vh = window.innerHeight;
      layers.forEach(function (el) {
        var speed = parseFloat(el.getAttribute('data-parallax')) || 0.15;
        var r = el.getBoundingClientRect();
        var progress = (r.top + r.height / 2 - vh / 2) / vh;
        el.style.setProperty('--py', (progress * speed * -100).toFixed(2) + 'px');
      });
      ticking = false;
    }
    function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(frame); } }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    frame();
  }

  /* ── counters (v1.0, unchanged) ──────────────────────────────────────── */
  function initCounters() {
    var els = document.querySelectorAll('[data-count-to]');
    if (!els.length) return;
    if (motionOff() || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.textContent = el.getAttribute('data-count-to'); });
      return;
    }
    var cio = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el = e.target, target = parseFloat(el.getAttribute('data-count-to'));
        var dec = (el.getAttribute('data-count-to').split('.')[1] || '').length;
        var start = performance.now(), dur = 1100;
        (function step(now) {
          var p = Math.min((now - start) / dur, 1);
          var eased = 1 - Math.pow(1 - p, 3);
          el.textContent = (target * eased).toFixed(dec);
          if (p < 1) requestAnimationFrame(step);
        })(start);
        cio.unobserve(el);
      });
    }, { threshold: 0.6 });
    els.forEach(function (el) { cio.observe(el); });
  }

  /* A readout that changes should travel, not teleport. Used by any section
     that swaps a value in place. Non-numeric values are set directly. */
  function countTo(el, target) {
    var to = parseFloat(String(target).replace(/[^\d.-]/g, ''));
    var from = parseFloat(String(el.textContent).replace(/[^\d.-]/g, ''));
    if (motionOff() || isNaN(to) || isNaN(from) || from === to) { el.textContent = target; return; }
    var decimals = (String(target).split('.')[1] || '').length;
    var start = performance.now(), span = 520;
    (function step(now) {
      var p = Math.min(1, (now - start) / span);
      var eased = 1 - Math.pow(1 - p, 3);
      el.textContent = (from + (to - from) * eased).toFixed(decimals);
      if (p < 1) requestAnimationFrame(step); else el.textContent = target;
    })(start);
  }

  window.ebMotion = { tag: tag, observe: observe, countTo: countTo, off: motionOff };

  function init(scope) { tag(scope); observe(scope); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); initParallax(); initCounters(); });
  } else { init(); initParallax(); initCounters(); }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  reduce.addEventListener('change', function () { observe(); });
  // The merchant's switch flips at runtime in the customiser; a MutationObserver
  // on <html> is cheaper than asking every section to listen for it.
  if ('MutationObserver' in window) {
    new MutationObserver(function () {
      if (motionOff()) slice(document.querySelectorAll('[data-reveal]')).forEach(reveal);
    }).observe(root, { attributes: true, attributeFilter: ['data-motion'] });
  }
})();
